import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const pageHeaderVariants = cva('group/page-header flex flex-col border-b bg-card text-card-foreground', {
  variants: {
    size: {
      default: '',
      compact: '',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const PageHeader = ({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof pageHeaderVariants>) => {
  return (
    <div data-slot="page-header" data-size={size} className={cn(pageHeaderVariants({ size, className }))} {...props} />
  );
};

const PageHeaderToolbar = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="page-header-toolbar"
      className={cn(
        'flex h-10 items-center gap-2 border-b px-4 group-data-[size=compact]/page-header:h-8 group-data-[size=compact]/page-header:px-3',
        className,
      )}
      {...props}
    />
  );
};

const PageHeaderHeading = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="page-header-heading"
      className={cn(
        'flex items-center justify-between gap-3 px-4 py-3 group-data-[size=compact]/page-header:px-3 group-data-[size=compact]/page-header:py-2',
        className,
      )}
      {...props}
    />
  );
};

const PageHeaderIcon = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="page-header-icon"
      className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted [&_svg]:size-4', className)}
      {...props}
    />
  );
};

const PageHeaderTitle = ({ className, ...props }: React.ComponentProps<'h1'>) => {
  return (
    <h1
      data-slot="page-header-title"
      className={cn(
        'font-medium text-foreground text-xl tracking-tight group-data-[size=compact]/page-header:text-lg',
        className,
      )}
      {...props}
    />
  );
};

const PageHeaderDescription = ({ className, ...props }: React.ComponentProps<'p'>) => {
  return (
    <p data-slot="page-header-description" className={cn('text-muted-foreground text-sm', className)} {...props} />
  );
};

const PageHeaderActions = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div data-slot="page-header-actions" className={cn('flex shrink-0 items-center gap-2', className)} {...props} />
  );
};

export {
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderIcon,
  PageHeaderTitle,
  PageHeaderToolbar,
  pageHeaderVariants,
};
